import { useEffect, useState } from "react";
import { NOTIFICATION_CATEGORIES, NotificationCategorySchema, type NotificationCategory, type UnsubscribeResponse } from "@starsach/shared-types";
import { postPublic } from "../api/client";
import { StarsachLockup } from "../components/Brand";
import { ErrorState, btnPrimary, btnSecondary } from "../components/ui";
import { loadConfig } from "../config";

/** The token rides in the fragment, which no browser sends to a server; it is read once and taken out of the address bar. */
function takeToken(): string {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "";
  if (token) window.history.replaceState(window.history.state, "", window.location.pathname);
  return token;
}

/** The category the link names, read to say what it turns off before asking. Only the server's check of its signature counts. */
export function categoryOfToken(token: string): NotificationCategory | undefined {
  try {
    const b64 = (token.split(".")[0] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4))) as { c?: unknown };
    const parsed = NotificationCategorySchema.safeParse(claims.c);
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * #649: the page a notice's "Unsubscribe from ..." link opens. No sign-in -
 * the signed link is the credential, and it can switch only the one
 * category it names for the one person it was sent to. Opening the page
 * changes nothing (mail scanners open links); the button does, and Undo
 * turns it back on. Mail providers that support RFC 8058 skip this page and
 * POST to the API directly.
 */
export function UnsubscribePage() {
  const [token] = useState(takeToken);
  const category = categoryOfToken(token);
  const label = NOTIFICATION_CATEGORIES.find((c) => c.id === category)?.label;
  const [state, setState] = useState<{ busy: boolean; result?: UnsubscribeResponse; error?: unknown }>({ busy: false });
  useEffect(() => {
    document.title = "Unsubscribe · Starsach";
  }, []);

  const set = async (email: boolean) => {
    setState((s) => ({ busy: true, ...(s.result ? { result: s.result } : {}) }));
    try {
      const config = await loadConfig();
      setState({ busy: false, result: await postPublic<UnsubscribeResponse>(config, "/v1/public/unsubscribe", { token, email }) });
    } catch (error) {
      setState((s) => ({ busy: false, error, ...(s.result ? { result: s.result } : {}) }));
    }
  };

  return (
    <main className="mx-auto max-w-lg px-4 py-12">
      <StarsachLockup className="h-10 w-auto text-ink dark:text-white" />
      <h1 className="mt-6 text-2xl font-semibold text-ink dark:text-white">Unsubscribe</h1>
      <div className="mt-4 space-y-3 rounded-xl border border-neutral-200 bg-white p-5 text-sm dark:border-neutral-800 dark:bg-neutral-900">
        {!token || !label ? (
          <p>This link is incomplete. Open it again from the email, or change what you get by email under Account › Notifications after you sign in.</p>
        ) : state.result ? (
          <>
            <div role="status" className="space-y-2">
              {state.result.email ? (
                <p>Turned back on. You will get “{state.result.label}” email again.</p>
              ) : (
                <p>Done. You will no longer get “{state.result.label}” email. Everything else you get from Starsach is as it was.</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!state.result.email && (
                <button type="button" className={btnSecondary} disabled={state.busy} onClick={() => void set(true)}>
                  {state.busy ? "Turning it back on…" : "Undo"}
                </button>
              )}
              <a className={btnSecondary} href="/account#notifications">Open Account › Notifications</a>
            </div>
          </>
        ) : (
          <>
            <p>
              This stops one kind of email from Starsach to you: <span className="font-medium">“{label}”</span>, as it is called under Account › Notifications. Everything else stays as it is, and you do not need to sign in.
            </p>
            <button type="button" className={btnPrimary} disabled={state.busy} onClick={() => void set(false)}>
              {state.busy ? "Unsubscribing…" : "Unsubscribe"}
            </button>
          </>
        )}
        {state.error !== undefined && <ErrorState error={state.error} lead={state.result ? "Couldn’t turn it back on." : "Couldn’t unsubscribe."} />}
      </div>
    </main>
  );
}
